import { useIntl } from 'react-intl';
import { Message } from '@rossum/api-client/shared';
import { FeatureIcon } from '@rossum/ui';
import { IconInfoSquareRounded } from '@rossum/ui/icons/tabler';
import { CircularProgress, Stack, Typography } from '@rossum/ui/material';
import HTMLMessage from '../../../../components/DatapointMessage/HTMLMessage';
import { UnpaidFeatureOverlay } from '../../../pricing/components/UnpaidFeatureOverlay';
import { NoAccessReason } from './resolveNoAccessReason';

type Props = {
  reason: NonNullable<NoAccessReason>;
  annotationMessages: Array<Message> | undefined;
};

export const NoAccessNotice = ({ reason, annotationMessages }: Props) => {
  const intl = useIntl();
  const isImporting = reason === 'importing';

  if (reason === 'restrictedAccess') {
    return (
      <Stack textAlign="center" height={1}>
        <UnpaidFeatureOverlay
          title={intl.formatMessage({
            id: 'features.pricing.unpaidFeatureOverlay.title.documentRestricted',
          })}
          dataCy="unpaid-overlay-document-restricted-sidebar"
          sx={{ p: 4 }}
        />
      </Stack>
    );
  }

  return (
    <Stack sx={{ px: 3, textAlign: 'center' }} alignItems="center" spacing={2}>
      {isImporting ? (
        <Stack p={3}>
          <CircularProgress data-cy={`no-data-icon-${reason}`} size={32} />
        </Stack>
      ) : (
        <FeatureIcon
          content={IconInfoSquareRounded}
          size="large"
          color="secondary"
          dataCy={`no-data-icon-${reason}`}
        />
      )}
      <Stack alignItems="center">
        <Typography variant="h6" data-cy={`no-data-title-${reason}`}>
          {intl.formatMessage({
            id: `components.documentValidation.sidebar.unable.${reason}`,
          })}
        </Typography>
        {(isImporting || reason === 'deleted') && (
          <Typography variant="body2" sx={{ maxWidth: 260 }}>
            {intl.formatMessage({
              id: `components.documentValidation.sidebar.unable.${reason}.text`,
            })}
          </Typography>
        )}
      </Stack>
      {annotationMessages && (
        <Typography variant="body2" color="text.secondary">
          {annotationMessages.map(({ content }) => (
            <div key={content} data-cy="no-data-message">
              <HTMLMessage content={content} />
            </div>
          ))}
        </Typography>
      )}
    </Stack>
  );
};
